import { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, Button } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { KeyboardArrowDown } from "@mui/icons-material";
import colors from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";

export interface ComoSelectOption {
  id: number | string;
  label: string;
  subtitle?: string;
  iconSrc?: string | null;
}

export interface ComoSelectProps {
  options: ComoSelectOption[];
  selectedOption?: ComoSelectOption;
  selectedOptions?: ComoSelectOption[];
  displayText?: string;
  onSelect?: (option: ComoSelectOption) => void;
  onMultiSelect?: (options: ComoSelectOption[]) => void;
  multiple?: boolean;
  placeholder?: string;
  disabled?: (option: ComoSelectOption) => boolean;
  maxHeight?: number;
  sx?: SxProps<Theme>;
}

export function ComoSelect({
  options,
  selectedOption,
  selectedOptions = [],
  displayText,
  onSelect,
  onMultiSelect,
  multiple = false,
  placeholder = "Select an option",
  disabled,
  maxHeight = 240,
  sx,
}: ComoSelectProps) {
  const { colors: workspace } = useWorkspaceColors();
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("comoAgentForm");

  const handleSelect = (option: ComoSelectOption) => {
    if (!disabled?.(option)) {
      if (multiple) {
        const isSelected = selectedOptions.some((s) => s.id === option.id);
        const nextSelected = isSelected
          ? selectedOptions.filter((s) => s.id !== option.id)
          : [...selectedOptions, option];
        onMultiSelect?.(nextSelected);
        return;
      }
      onSelect?.(option);
      setIsOpen(false);
    }
  };

  const selectedText =
    displayText ??
    (multiple
      ? selectedOptions.length > 0
        ? `${selectedOptions
            .slice(0, 4)
            .map((option) => option.label)
            .join(", ")}${
            selectedOptions.length > 4
              ? ` + ${selectedOptions.length - 4} ${t("more")}`
              : ""
          }`
        : placeholder
      : selectedOption
        ? selectedOption.label
        : placeholder);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Box
        sx={{ position: "relative", width: "100%", overflow: "visible" }}
        ref={selectRef}
      >
        <Button
          onClick={() => setIsOpen((prev) => !prev)}
          fullWidth
          sx={{
            py: 1.25,
            px: 2,
            bgcolor: "white",
            border: `1px solid ${colors.gray300}`,
            borderRadius: 2,
            justifyContent: "space-between",
            textTransform: "none",
            color: colors.primary_dark,
            alignItems: "center",
            "&:hover": { bgcolor: colors.gray50 },
            "&:focus": {
              outline: "none",
              borderColor: workspace.primary_dark,
            },
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: isOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              flex: 1,
              gap: 1,
            }}
          >
            {!multiple && selectedOption?.iconSrc && (
              <img
                src={selectedOption.iconSrc}
                alt=""
                style={{ width: 18, height: 18, objectFit: "contain" }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: colors.primary_dark,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {selectedText}
              </Typography>
              {!multiple && selectedOption?.subtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.gray600,
                    fontSize: "0.7rem",
                    fontStyle: "italic",
                  }}
                >
                  {selectedOption.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </Button>

        {isOpen && (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              zIndex: 9999,
              width: "100%",
              mt: 0.5,
              maxHeight: maxHeight,
              overflowY: "auto",
              borderRadius: 2,
              border: `1px solid ${colors.gray300}`,
            }}
          >
            {options.map((option) => {
              const isDisabled = disabled?.(option);
              const isSelected = multiple
                ? selectedOptions.some((s) => s.id === option.id)
                : selectedOption?.id === option.id;
              return (
                <Box
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    bgcolor: isSelected
                      ? `${workspace.primary_dark}0F`
                      : "white",
                    borderLeft: isSelected
                      ? `4px solid ${workspace.accent}`
                      : "4px solid transparent",
                    opacity: isDisabled ? 0.6 : 1,
                    "&:hover": {
                      bgcolor: isDisabled
                        ? undefined
                        : `${workspace.primary_dark}0F`,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {option.iconSrc && (
                      <img
                        src={option.iconSrc}
                        alt=""
                        style={{ width: 18, height: 18, objectFit: "contain" }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 600 : 500,
                        color: isDisabled ? colors.gray400 : colors.gray900,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                  {option.subtitle && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.gray600 }}
                    >
                      {option.subtitle}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
